import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  Icon,
  Skeleton,
  Spinner,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';

import type {
  Commonv1Value,
  V1AutomationConfig,
  V1CallArg,
  V1CredentialInfo,
  V1Step,
} from '@/generated/hooks/grimoire/models';
import type { IStepRunIO } from '@/modules/automation/hooks/useFocusedTestRunStatus';
import { matchCallResults } from '@/modules/automation/utils/match-call-results';
import { resolveCallArgs } from '@/modules/automation/utils/resolve-call-args';
import { resolveStepConfigs } from '@/modules/automation/utils/resolve-step-config';
import { resolveStepConnections } from '@/modules/automation/utils/resolve-step-connections';
import type { TStepRunStatus } from '@/modules/automation/utils/step-run-status';
import { BookSpineIcon } from '@/modules/automation-run/components/run-log/RunLogExternalCalls';
import { useBookIdentity } from '@/modules/automation-run/hooks/useBookIdentity';
import { invocationKey } from '@/modules/automation-run/hooks/useRunJournalEvents';
import type { IResolvedExternalCall } from '@/modules/automation-run/utils/external-call-visibility';
import { PreviewLayover } from '@/modules/outputs/components/PreviewLayover';
import { RunTableOutputPreview } from '@/modules/outputs/components/RunTableOutputPreview';
import {
  EOutputCardVariant,
  type TResolvedValue,
} from '@/modules/outputs/types';
import { getOutputCardVariant } from '@/modules/outputs/components/output-card-utils';
import { ValueList } from '@/shared/components/ValueList';
import { formatDisplayName } from '@/shared/utils';
import { formatProcedureLabel } from '@/shared/utils/procedure-label';

import { ManifestConfigTab } from '@/modules/automation/components/AutomationEditor/ManifestConfigTab';
import { DraftStepRules } from '@/modules/automation/components/AutomationEditor/DraftStepSummary';

const MAX_INPUT_ROWS = 4;
const MAX_OUTPUT_BOXES = 3;
const SECRET_MASK = '••••••••';

/** Where a planned call stands in the run that's open, if any. */
type TCallState =
  | 'plan'
  | 'ran'
  | 'running'
  | 'notRunYet'
  | 'notCalled'
  | 'stopped';

/** A white card inside the selected-step panel, with a title and optional
 * description. */
function StepPanelSection({
  title,
  name,
  description,
  children,
}: {
  title: string;
  name: string;
  description?: string;
  children?: ReactNode;
}) {
  return (
    <Flex
      direction="column"
      align="stretch"
      gap={4}
      width="full"
      className="rounded-xl bg-card p-4"
      data-testid={`draft-step-section-${name}`}
    >
      <Flex direction="column" align="stretch" gap={0.5} width="full">
        <Text level="small" weight="medium">
          {title}
        </Text>
        {description && (
          <Text level="xSmall" color="muted">
            {description}
          </Text>
        )}
      </Flex>
      {children}
    </Flex>
  );
}

function SubLabel({ children }: { children: ReactNode }) {
  return (
    <Text level="xSmall" color="muted">
      {children}
    </Text>
  );
}

/** Heads a call's Uses or Returns; heavier than a field's own label. */
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Text level="xSmall" color="muted" weight="medium">
      {children}
    </Text>
  );
}

/** One output box: a list item, a record field, or the whole value. */
interface IReturnBox {
  key: string;
  label?: string;
  value: TResolvedValue;
}

/** Splits a result into boxes: a list per item, a record per field (empty
 * fields skipped), anything else, like a file or a table, whole. */
function toReturnBoxes(value: TResolvedValue): IReturnBox[] {
  if (Array.isArray(value) && value.length > 0) {
    return value.map((item, index) => ({ key: `item-${index}`, value: item }));
  }
  if (getOutputCardVariant(value) === EOutputCardVariant.DICT) {
    return Object.entries(value as Record<string, TResolvedValue>)
      .filter(([, field]) => field !== null)
      .map(([field, fieldValue]) => ({
        key: field,
        label: formatDisplayName(field),
        value: fieldValue,
      }));
  }
  return [{ key: 'value', value }];
}

/** A call's result in the run log's output boxes, one per item or field, so
 * each keeps its own preview. */
function ReturnBoxes({
  name,
  boxes,
  onOpen,
}: {
  name: string;
  boxes: IReturnBox[];
  onOpen: (name: string, value: TResolvedValue) => void;
}) {
  const { t: tAutomation } = useTranslation('automation');
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? boxes : boxes.slice(0, MAX_OUTPUT_BOXES);

  return (
    <Flex direction="column" align="stretch" gap={1.5}>
      {visible.map((box, index) => {
        const boxName = box.label
          ? `${name} · ${box.label}`
          : boxes.length > 1
            ? `${name} · ${index + 1}`
            : name;
        return (
          <Flex key={box.key} direction="column" align="stretch" gap={1}>
            {box.label && <SubLabel>{box.label}</SubLabel>}
            <RunTableOutputPreview
              name={boxName}
              value={box.value}
              onOpen={() => onOpen(boxName, box.value)}
              variant="compact"
            />
          </Flex>
        );
      })}
      {boxes.length > MAX_OUTPUT_BOXES && (
        <Button
          type="button"
          variant="link"
          size="xs"
          className="self-start px-0"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll
            ? tAutomation('automation.draftRail.action.showLess')
            : tAutomation('automation.draftRail.action.showMore', {
                count: boxes.length - visible.length,
              })}
        </Button>
      )}
    </Flex>
  );
}

/** One row: a call site in the plan, or one call in a run. */
interface IActionCall {
  key: string;
  bookName?: string;
  fallbackName?: string;
  functionName: string;
  args: V1CallArg[] | undefined;
  result?: IResolvedExternalCall;
  state: TCallState;
}

/** One call in the run log's external-call format: logo, action name, its
 * inputs, then what it returned once a run has reached it. */
function ActionRow({
  call,
  automationConfig,
  onOpenResult,
}: {
  call: IActionCall;
  automationConfig?: V1AutomationConfig;
  onOpenResult: (name: string, value: TResolvedValue) => void;
}) {
  const { t: tAutomation } = useTranslation('automation');
  const {
    bookName,
    fallbackName,
    functionName,
    args: plannedArgs,
    result: current,
    state,
  } = call;
  const { displayName } = useBookIdentity(bookName);
  const actionLabel = formatProcedureLabel(functionName, tAutomation);
  const args = resolveCallArgs(
    plannedArgs,
    automationConfig,
    current?.call.inputs,
  );
  const returnBoxes =
    current?.output != null ? toReturnBoxes(current.output) : [];
  const isInactive = state === 'notCalled' || state === 'notRunYet';

  return (
    <Flex
      align="start"
      gap={2}
      width="full"
      data-testid={`draft-step-planned-action-${functionName}`}
      data-state={state}
    >
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <Flex className="shrink-0">
            <BookSpineIcon bookName={bookName} />
          </Flex>
        </TooltipTrigger>
        <TooltipContent side="left">
          {displayName || bookName || fallbackName}
        </TooltipContent>
      </Tooltip>
      {/* Lattice defaults column children to `items-start`, which stops the name truncating. */}
      <Flex
        direction="column"
        align="stretch"
        gap={0.5}
        className="min-w-0 flex-1"
      >
        {/* `min-h-6` centres the 12px name on the 24px logo. */}
        <Flex align="center" gap={1.5} className="min-h-6 min-w-0">
          <Text
            as="span"
            level="xSmall"
            weight="medium"
            title={functionName}
            className="min-w-0 truncate"
          >
            {actionLabel}
          </Text>
          {state === 'running' && (
            <Flex align="center" gap={1} className="ml-auto shrink-0">
              <Spinner size="sm" />
              <Text as="span" level="xSmall" color="muted">
                {tAutomation('automation.draftRail.action.running')}
              </Text>
            </Flex>
          )}
        </Flex>

        {isInactive && (
          <Text level="xSmall" color="muted">
            {tAutomation(
              state === 'notCalled'
                ? 'automation.draftRail.action.notCalled'
                : 'automation.draftRail.action.notRunYet',
            )}
          </Text>
        )}

        {!isInactive && (
          <Flex direction="column" align="stretch" gap={3}>
            {args.length > 0 && (
              <Flex direction="column" align="stretch" gap={1}>
                <SectionLabel>
                  {tAutomation('automation.draftRail.action.uses')}
                </SectionLabel>
                <ValueList
                  entries={args.map(({ name, value }) => [name, value])}
                  ariaLabel={actionLabel}
                  placeholders={Object.fromEntries(
                    args
                      .filter(({ secret }) => secret)
                      .map(({ name }) => [name, SECRET_MASK]),
                  )}
                  tags={Object.fromEntries(
                    args
                      .filter(({ fromTweak }) => fromTweak)
                      .map(({ name }) => [
                        name,
                        tAutomation('automation.draftRail.action.tweak'),
                      ]),
                  )}
                  maxRows={MAX_INPUT_ROWS}
                  interactive={false}
                />
              </Flex>
            )}

            {(state === 'ran' ||
              state === 'stopped' ||
              state === 'running') && (
              <Flex direction="column" align="stretch" gap={1}>
                <SectionLabel>
                  {tAutomation('automation.draftRail.action.returns')}
                </SectionLabel>
                {state === 'running' ? (
                  <Skeleton
                    className="h-12 w-full rounded-lg"
                    data-testid="draft-step-action-returns-loading"
                  />
                ) : state === 'stopped' ? (
                  <Text level="xSmall" color="muted">
                    {tAutomation('automation.draftRail.action.stopped')}
                  </Text>
                ) : returnBoxes.length > 0 ? (
                  <ReturnBoxes
                    name={actionLabel}
                    boxes={returnBoxes}
                    onOpen={onOpenResult}
                  />
                ) : (
                  <Text level="xSmall" color="muted">
                    {tAutomation('automation.draftRail.action.nothingReturned')}
                  </Text>
                )}
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

/**
 * Places each planned call in the open run. Without a per-call "started" event
 * the call in flight isn't known, so a running (or stopped) step's first call
 * without a result, in source order, stands for it; later ones haven't run.
 */
function assignCallStates(
  hasResults: boolean[],
  status: TStepRunStatus | undefined,
): TCallState[] {
  let inFlightAssigned = false;
  return hasResults.map(ran => {
    if (!status) return 'plan';
    if (ran) return 'ran';
    if (status === 'pending') return 'notRunYet';
    if (status === 'done') return 'notCalled';
    if (!inFlightAssigned) {
      inFlightAssigned = true;
      return status === 'running' ? 'running' : 'stopped';
    }
    return status === 'running' ? 'notRunYet' : 'notCalled';
  });
}

interface ISelectedStepPanelProps {
  step: V1Step;
  automationConfig?: V1AutomationConfig;
  onSaveConfigValue?: (name: string, value: Commonv1Value) => Promise<void>;
  credentialsByAlias: ReadonlyMap<string, V1CredentialInfo>;
  /** Set when a run is open; absent in the plain authoring view. */
  status?: TStepRunStatus;
  io?: IStepRunIO;
  /** Calls a still-running step has already finished. */
  runningCalls?: IResolvedExternalCall[];
  onTestStep?: (stepId: string) => void;
  /**
   * Leads the panel above its blocks, e.g. the step's badge and title. The
   * draft view leaves it out because the document beside the rail shows the
   * step; a map has no document, so it passes one.
   */
  header?: ReactNode;
  /**
   * Opens with a Description block — the overview under the heading, the
   * rules inside — styled like the blocks that follow it.
   */
  showDescription?: boolean;
  /**
   * Leaves out the needs-guidance pill. The run map shows the exception in the
   * step's own log, so the rail would only be saying it twice.
   */
  hideException?: boolean;
}

/** The rail's view of the selected step: its planned actions and tweaks, plus
 * the step's own inputs and output once a run is open. No header;
 * re-selecting the step in the document closes it. */
export function SelectedStepPanel({
  step,
  automationConfig,
  onSaveConfigValue,
  credentialsByAlias,
  status,
  io,
  runningCalls,
  onTestStep,
  header,
  showDescription = false,
  hideException = false,
}: ISelectedStepPanelProps) {
  const { t: tAutomation } = useTranslation('automation');
  const [openResult, setOpenResult] = useState<{
    name: string;
    value: TResolvedValue;
  }>();
  const connections = resolveStepConnections(
    step.audit?.external_calls,
    credentialsByAlias,
  );
  const configs = resolveStepConfigs(step.audit, automationConfig);
  const plannedCalls = connections.flatMap(connection =>
    connection.calls.map(({ functionName, call }) => ({
      functionName,
      call,
      book: connection.book,
      alias: connection.connectionAlias,
    })),
  );
  // Run data without a status still means the step ran.
  const runStatus = status ?? (io ? 'done' : undefined);
  const { byCall, unmatched } = matchCallResults(
    plannedCalls,
    io?.externalCalls ?? (runStatus === 'running' ? runningCalls : undefined),
  );
  const states = assignCallStates(
    plannedCalls.map(({ call }) => (byCall.get(call)?.length ?? 0) > 0),
    runStatus,
  );
  // Once a run is open, a row per call, so a loop shows each time it ran.
  const calls: IActionCall[] = plannedCalls.flatMap(
    ({ functionName, call, book, alias }, i) => {
      const site = {
        bookName: book,
        fallbackName: alias,
        functionName,
        args: call.args,
      };
      const results = byCall.get(call) ?? [];
      if (results.length === 0) {
        return [
          {
            ...site,
            key: `${functionName}-${call.call_location?.start_byte}`,
            state: states[i],
          },
        ];
      }
      return results.map(result => ({
        ...site,
        key: invocationKey(result.call),
        result,
        state: 'ran' as const,
      }));
    },
  );
  const unmatchedCalls: IActionCall[] = unmatched.map(result => ({
    key: invocationKey(result.call),
    bookName: result.call.bookFunction?.bookName,
    functionName:
      result.call.bookFunction?.functionName ?? result.call.functionName,
    args: undefined,
    result,
    state: 'ran',
  }));
  const actionCount = plannedCalls.length + unmatched.length;
  const runInputs = io?.inputs ?? [];
  const hasRunOutput = io?.output != null;
  const runOutputBoxes = io?.output != null ? toReturnBoxes(io.output) : [];
  const hasTweaks = configs.length > 0 && Boolean(onSaveConfigValue);
  // Only set on the step a run stopped on.
  const exception = hideException ? undefined : io?.errorText;
  const hasRules = (step.rules?.length ?? 0) > 0;
  const isEmpty =
    !exception &&
    actionCount === 0 &&
    !hasTweaks &&
    runInputs.length === 0 &&
    !hasRunOutput;
  const openResultPreview = (name: string, value: TResolvedValue) =>
    setOpenResult({ name, value });

  return (
    <Flex
      direction="column"
      width="full"
      className="draft-selection-fill min-h-0 min-w-0 flex-1 overflow-hidden rounded-xl"
      data-testid="draft-selected-step-panel"
    >
      <Flex
        direction="column"
        align="stretch"
        gap={3}
        width="full"
        // `pb-6` lets the last row clear the fade once scrolled to the end.
        className="no-scrollbar min-h-0 min-w-0 flex-1 overflow-y-auto px-3 pt-3 pb-6 [mask-image:linear-gradient(to_bottom,black_calc(100%_-_1.5rem),transparent)]"
      >
        {header && (
          <Flex
            direction="column"
            align="stretch"
            width="full"
            className="px-1 pt-1"
            data-testid="draft-step-header"
          >
            {header}
          </Flex>
        )}

        {exception && (
          <Flex
            direction="column"
            align="stretch"
            gap={2}
            width="full"
            className="min-w-0 rounded-xl bg-warning/10 p-3"
          >
            <Text level="small" weight="semibold" className="text-warning">
              {tAutomation('automation.draftRail.needsGuidance')}
            </Text>
            <Text
              level="xSmall"
              color="muted"
              ellipsis={{ lines: 4, tooltip: true }}
              className="min-w-0 break-words break-all"
            >
              {exception}
            </Text>
          </Flex>
        )}

        {showDescription && (step.overview || hasRules) && (
          <StepPanelSection
            title={tAutomation('automation.manifestSopView.description')}
            name="description"
            description={step.overview}
          >
            {hasRules && (
              <Flex direction="column" align="stretch" width="full">
                <DraftStepRules step={step} selected={false} />
              </Flex>
            )}
          </StepPanelSection>
        )}

        {runInputs.length > 0 && (
          <StepPanelSection
            title={tAutomation('automation.draftRail.stepInputs')}
            name="run-inputs"
          >
            <ValueList
              entries={runInputs}
              ariaLabel={tAutomation('automation.draftRail.stepInputs')}
              maxRows={MAX_INPUT_ROWS}
              interactive={false}
            />
          </StepPanelSection>
        )}

        {actionCount > 0 && (
          <StepPanelSection
            title={tAutomation('automation.draftRail.plannedActions')}
            name="planned-actions"
            description={tAutomation('automation.draftRail.plannedActionsHint')}
          >
            <Flex direction="column" align="stretch" gap={5} width="full">
              {calls.map(call => (
                <ActionRow
                  key={call.key}
                  call={call}
                  automationConfig={automationConfig}
                  onOpenResult={openResultPreview}
                />
              ))}
              {unmatched.length > 0 && (
                <SectionLabel>
                  {tAutomation('automation.draftRail.action.alsoCalled')}
                </SectionLabel>
              )}
              {unmatchedCalls.map(call => (
                <ActionRow
                  key={call.key}
                  call={call}
                  automationConfig={automationConfig}
                  onOpenResult={openResultPreview}
                />
              ))}
            </Flex>
          </StepPanelSection>
        )}

        {hasRunOutput && (
          <StepPanelSection
            title={tAutomation('automation.draftRail.stepOutput')}
            name="run-output"
          >
            {runOutputBoxes.length > 0 ? (
              <ReturnBoxes
                name={tAutomation('automation.draftRail.stepOutput')}
                boxes={runOutputBoxes}
                onOpen={openResultPreview}
              />
            ) : (
              <Text level="xSmall" color="muted">
                {tAutomation('automation.draftRail.action.nothingReturned')}
              </Text>
            )}
          </StepPanelSection>
        )}

        {hasTweaks && onSaveConfigValue && (
          <StepPanelSection
            title={tAutomation('automation.draftRail.tweaks')}
            name="tweaks"
          >
            <ManifestConfigTab configs={configs} onSave={onSaveConfigValue} />
          </StepPanelSection>
        )}

        {isEmpty && (
          <Flex
            align="center"
            justify="center"
            width="full"
            className="flex-1 p-4 text-center"
            data-testid="draft-step-empty"
          >
            <Text level="xSmall" color="muted">
              {tAutomation('automation.draftRail.noStepDetail')}
            </Text>
          </Flex>
        )}
      </Flex>

      {onTestStep && step.entry_function?.name && (
        <Flex
          width="full"
          className="shrink-0 px-3 pt-2 pb-3"
          data-testid="draft-selected-step-footer"
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (step.entry_function?.name)
                onTestStep(step.entry_function.name);
            }}
            className="w-full bg-card"
          >
            <Icon type="FlaskConical" size="xs" />
            {tAutomation('automation.draftRail.testStep')}
          </Button>
        </Flex>
      )}

      <PreviewLayover
        isOpen={openResult !== undefined}
        outputName={openResult?.name ?? ''}
        outputValue={openResult?.value ?? null}
        onClose={() => setOpenResult(undefined)}
      />
    </Flex>
  );
}
